<template>
  <section class="relative flex xl:justify-center w-full z-0">
    <DocumentIndex
      v-if="isMounted"
      v-model="pageIndex"
      :pages="pagesList"
      @section-id-changed="activeSectionId = $event"
    />

    <div
      id="document-page"
      class="document-page"
    >
      <TextEditorInput
        v-show="showFakeToolBar"
        id="fake-tinymce"
        :inline="false"
        :height="1"
      />

      <div
        id="tinymce-toolbar"
        ref="realToolbarRef"
        :class="{'hidden': showFakeToolBar}"
      />

      <!-- Save button -->
      <AppButton
        class="save-plan-button btn-solid btn-primary"
        :processing="saving"
        @click="savePlan"
      >
        {{ saving ? 'Saving...' : 'Save' }}
      </AppButton>

      <component
        :is="currentPage.component"
        v-show="!pageLoading"
      />

      <AppContentLoader v-show="pageLoading" />
    </div>
  </section>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { computed, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute } from 'vue-router';

import { AppContentLoader, AppButton } from '@/components/app';
import { DocumentIndex } from '@/components/plan-document/components';
import { TextEditorInput } from '@/components/form-inputs';
import * as Pages from '@/components/plan-document/pages';

import { useHttp, useString, useToasts } from '@/composables';
import { usePlanStore } from '@/store';

const props = defineProps({
  frameworks: {
    type: Array,
    default: () => [],
  },
});

// Auto adds inline to all tinymce unless specified
provide('isDocumentContext', true);

const activeSectionId = ref(null);
provide('activeSectionId', activeSectionId);

const planStore = usePlanStore();
const string = useString();
const route = useRoute();
const toasts = useToasts();

let observer = null;

const documentPages = {
  coverPage: {
    name: 'Cover Page',
    component: Pages.DocumentCoverPage,
    sections: [
      { name: 'Logo' },
    ],
  },
  brandingSettings: {
    name: 'Branding Settings',
    component: Pages.DocumentBrandingPage,
    sections: [],
  },
  preliminaries: {
    name: 'Preliminaries',
    component: Pages.DocumentPreliminariesPage,
    sections: [
      { name: 'List of abbreviations' },
      { name: 'Message from the Mayor' },
      { name: 'Message from the Municipal Manager' },
    ],
  },
  listOfFigures: {
    name: 'List of figures',
    component: Pages.DocumentListOfFiguresPage,
    sections: [],
  },
  executiveSummary: {
    name: 'Executive Summary',
    component: Pages.DocumentExecutiveSummaryPage,
    sections: [
      { name: 'Who are we?' },
      { name: 'What are the issues we are facing?' },
      { name: 'What opportunities do we offer?' },
      { name: 'What are we doing to improve service delivery over the next 5 years?' },
      { name: 'Priorities of council' },
      { name: 'What can you expect from us?' },
      { name: 'How will our progress be measured?' },
      { name: 'How was this IDP developed?' },
    ],
  },
  introduction: {
    name: 'Introduction',
    component: Pages.DocumentIntroductionPage,
    sections: [
      { name: 'Powers and functions' },
    ],
  },
  visionMissionValues: {
    name: 'Vision, Mission, Values, Goals and Objectives',
    component: Pages.DocumentVisionMissionEtcPage,
    sections: [
      { name: 'Vision' },
      { name: 'Mission' },
      { name: 'Values' },
      { name: 'Goals' },
      { name: 'Objectives' },
    ],
  },
  situationalAnalysis: {
    name: 'Situational Analysis',
    component: Pages.DocumentSituationalAnalysisPage,
    sections: [
      { name: 'Overview of the municipality' },
      { name: 'Demographic profile' },
      { name: 'Socio-economic profile' },
      { name: 'Economic profile' },
      { name: 'Service delivery and infrastructure' },
      { name: 'Finance and governance' },
      { name: 'Environmental context' },
      { name: 'Overview of community needs and ward priorities' },
    ],
  },
  institutionalFramework: {
    name: 'Institutional Framework',
    component: Pages.DocumentInstitutionalFrameworkPage,
    sections: [
      { name: 'Governance' },
      { name: 'Municipal structure' },
      { name: 'Skills requirements' },
      { name: 'Strengths and weaknesses' },
      { name: 'Employment equity' },
    ],
  },
  spatialDevelopmentFramework: {
    name: 'Spatial Development Framework',
    component: Pages.DocumentSpatialDevelopmentFrameworkPage,
    sections: [
      { name: 'Spatial, economic and development rationale' },
      { name: 'Desired spatial outcome' },
      { name: 'Spatial strategies' },
    ],
  },
  developmentStrategies: {
    name: 'Development Strategies',
    component: Pages.DocumentDevelopmentStrategiesPage,
    sections: [],
  },
  implementationPlan: {
    name: 'Implementation Plan',
    component: Pages.DocumentImplementationPlanPage,
    sections: [
      { name: 'Overview of implementation plan' },
      { name: 'Municipal programmes and projects' },
      { name: 'Programmes and projects of other organs of state' },
      { name: 'Local Economic Development' },
      { name: 'Disaster management plan' },
      { name: 'Climate change response implementation plan' },
    ],
  },
  financialPlan: {
    name: 'Financial Plan',
    component: Pages.DocumentFinancialPlanPage,
    sections: [
      { name: 'Financial strategy' },
      // Explicit ids: the heading text differs from each budget table's anchor element
      { name: 'Summary of expenditure by development strategy - operating budget', id: 'budget-per-development-strategy' },
      { name: 'Summary of expenditure by development strategy - capital budget', id: 'capital-budget' },
      { name: 'Projection of financial performance' },
      { name: 'Capital expenditure and financing' },
      { name: 'Projection of financial position' },
      { name: 'Projection of cash flow' },
      { name: 'Comments to the financial plan' },
    ],
  },
  keyPerformanceIndicators: {
    name: 'Performance Monitoring Framework',
    component: Pages.DocumentKeyPerformanceIndicatorsPage,
    sections: [],
  },
  annexures: {
    name: 'Annexures',
    component: Pages.DocumentAnnexuresPage,
    sections: [
      { name: 'Required Annexures' },
      { name: 'Additional Annexures' },
    ],
  },
};

// Assign each section an ID by sluggifying its page name and section name
Object.keys(documentPages).forEach(pageKey => {
  documentPages[pageKey].sections.forEach(section => {
    section.id ??= string.sluggify(section.name);
  });
});

const pageKeys = Object.keys(documentPages);
const STORAGE_KEY = `plan_document_page_${planStore.id}`;
const savedKey = localStorage.getItem(STORAGE_KEY);
const initialKey = (savedKey && pageKeys.includes(savedKey)) ? savedKey : pageKeys[0];
const currentKey = ref(initialKey);

const isMounted = ref(false);
const pageLoading = ref(true);

const isDirty = ref(false);
const saving = ref(false);

const realToolbarRef = ref(null);
const showFakeToolBar = ref(true);

const selectedGoals = ref([]);
const selectedFramework = ref(null);

const currentPage = computed(() => documentPages[currentKey.value]);
const pageIndex = computed({
  get: () => {
    return pageKeys.indexOf(currentKey.value);
  },
  set: (newIndex) => {
    if (newIndex >= 0 && newIndex < pageKeys.length) {
      loadPage();

      setTimeout(() => {
        currentKey.value = pageKeys[newIndex];
      }, 50);
    }
  },
});

const pagesList = computed(() => {
  return pageKeys.map(key => documentPages[key]);
});

// Handle browser refresh or tab close - triggers browser warning
const handleBeforeUnload = (e) => {
  if (isDirty.value) {
    e.preventDefault();
    e.returnValue = '';
  }
};

// Handle in-app router navigation
// This triggers a standard JS confirm, dialog when navigating to other views
onBeforeRouteLeave((to, from, next) => {
  if (isDirty.value && !import.meta.env.DEV) {
    const answer = window.confirm('You have unsaved changes. Are you sure you want to leave? Click Save to keep your work.');

    if (answer) {
      next(); // Proceed
    } else {
      next(false); // Cancel navigation
    }
  } else {
    next(); // No changes, proceed normaly
  }
});

onMounted(() => {
  isMounted.value = true;
  loadPage();

  window.addEventListener('keydown', handleKeyDown);
  // Listener for browser close/refresh event
  window.addEventListener('beforeunload', handleBeforeUnload);
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
  // Clean up listeners to prevent memory leaks
  window.removeEventListener('keydown', handleKeyDown);
  window.removeEventListener('beforeunload', handleBeforeUnload);
});

const loadPage = () => {
  pageLoading.value = true;

  setTimeout(() => {
    pageLoading.value = false;
  }, 100);
};

const updateToolbarStatus = () => {
  const el = realToolbarRef.value;
  if (!el) return;

  const children = el.children;

  // If no children, show fake
  if (children.length === 0) {
    showFakeToolBar.value = true;
    return;
  }

  // if any are visible
  let hasVisibleContent = false;

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    const style = window.getComputedStyle(child);

    const isHidden = style.display === 'none' || style.visibility === 'hidden';

    if (!isHidden) {
      hasVisibleContent = true;
      break;
    }
  }

  // If we found nothing visible, SHOW the fake toolbar.
  showFakeToolBar.value = !hasVisibleContent;
};

// init goals
const populateSelectedGoals = async () => {
  if (selectedFramework.value) {
    selectedGoals.value = selectedFramework.value.goals.map(goal => goal.id);
  } else {
    const requests = props.frameworks.map(async (framework) => {
      const response = await useHttp().get(`/util/frameworks/${framework.id}/goals`);

      if (response.statusCode === HttpStatusCode.Ok) {
        return response.data;
      }

      return [];
    });

    const results = await Promise.all(requests);

    selectedGoals.value = results
      .flat()
      .map(goal => goal.id);
  }
};

const savePlan = async () => {
  saving.value = true;

  const response = await useHttp().put(`/municipalities/${route.params.municipality}/plan`, planStore.formData);

  if (response.statusCode === HttpStatusCode.Ok) {
    toasts.success('Plan saved successfully');
    isDirty.value = false;
  } else if (response.statusCode !== HttpStatusCode.Conflict) {
    // A 409 (locked plan) is handled centrally by the HTTP interceptor; only handle other failures here.
    toasts.error('Failed to save Plan');
  }

  saving.value = false;
};

const handleKeyDown = (e) => {
  // Check if "s" is pressed AND (Control key OR Command key for Mac)
  if ((e.ctrlKey || e.metaKey) && e.key === 's') {
    e.preventDefault(); // Stop the "Save Page As" dialog

    savePlan();
  }
};

// Persistent pages on reload and spinner until tinymce has loaded
watch(currentKey, (newKey) => {
  localStorage.setItem(STORAGE_KEY, newKey);
});

watch(selectedFramework, () => {
  populateSelectedGoals();
}, { immediate: true });

// Watch the store for any changes to set the dirty flag
watch(() => planStore.formData, () => {
  isDirty.value = true;
}, { deep: true });

// Watcher ensures we attach observer when the div appears in the DOM
watch(realToolbarRef, (newEl) => {
  if (observer) observer.disconnect();

  if (newEl) {
    // Run check immediately
    updateToolbarStatus();

    // Start observing
    observer = new MutationObserver(updateToolbarStatus);
    observer.observe(newEl, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'visibility'],
    });
  } else {
    // If element is gone, show fake by default
    showFakeToolBar.value = true;
  }
});
</script>
